import React, { FC, ReactElement, useEffect, useState } from 'react';
import { Grid, useMediaQuery } from '@mui/material';
import { AppContext } from '../../context/AppContext';
import BakeasyCollapsibleTable from '../../components/tables/BakeasyCollapsibleTable';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import BakeasyPaper from '../../components/common/BakesasyPaper';
import * as Button from '../../components/buttons/BakeasyButtons';
import BakeasyHeaderLabel from '../../components/labels/BakeasyHeaderLabel';
import {
  downloadInvoices,
  fetchInvoices,
  fetchInvoicesInnerTable,
  invoiceState
} from '../../redux_store/slices/invoicesSlice';
import { useAppDispatch, useAppSelector } from '../../redux_store/reduxStore';
import { formatDateTime } from '../../utils/DateUtils';
import { showErrorSnackbar } from '../../redux_store/slices/snackbarSlice';
import { BakeasyChip } from '../../components/common/BakeasyChip';
import * as TextField from '../../components/textfields/BakeasyTextFields';
import BakeasyEmptyMsg from '../../components/common/BakeasyEmptyMsg';
import BakeasySelectedFilters from '../../components/common/BakeasySelectedFilters';
import { invoiceTableHeaders, reportTableHeaders } from '../../constants/TableHeaders';
import { deleteKeyFromObject, preparePayload } from '../../utils/commonUtils';
import { useDebouncedFetch } from '../../custom/useDebouncedCallback';

export const Invoices: FC = (): ReactElement => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const fetchData = useDebouncedFetch();
  const tableRef = React.useRef<any>();
  let myContext = React.useContext(AppContext);
  const isMobile = useMediaQuery('(max-width:600px)');

  const [count, setCount] = React.useState<number>(0);
  const [page, setPage] = React.useState<number>(0);
  const [search, setSearch] = useState<string>('');
  const [invoiceList, setInvoiceList] = useState<any[]>([]);
  const [invoiceHistoryList, setInvoiceHistoryList] = useState<any[]>([]);
  const [tFilter, setTFilter] = React.useState<any>({});

  let { error, filter, pageNo, rowsPerPage, value, historyValue } = useAppSelector(invoiceState);

  const refreshPageData = async (newPage: number, search?: string, filter?: {} | undefined) => {
    let payload = await preparePayload(
      { limit: rowsPerPage, page: newPage + 1, sortBy: 'invoiceId', sortDirection: -1 },
      filter,
      search,
      newPage
    );

    if (payload['sortBy'] === 'createdDate') {
      payload['sortBy'] = 'invoiceId';
    }

    await dispatch(fetchInvoices(payload));
    // await fetchData(fetchInvoices, payload);

  };

  // useEffect(() => {
  //   return () => fetchData.cancel(); // Cancel debounce on component unmount to avoid memory leaks
  // }, [fetchData]);

  useEffect(() => {
    if (value?.totalCount === -1) {
      refreshPageData(pageNo);
    } else {
      const filteredData: any = value?.data?.map((invoice: any) => {
        return {
          id: invoice?._id,
          icon: 'ICONN',
          onIconClick: () => {
            if (invoice?.isSubOrderPresent === true) {
              handleHistoryRowIconClick({ id: invoice._id });
            } else {
              dispatch(showErrorSnackbar({ message: 'NO_SUBORDERS_PRESENT' }));
            }
          },
          invoiceId: invoice?.invoiceId,
          orderId: invoice?.orderId,
          name: invoice?.storeName,
          onInvoiceIdClick: () => {
            navigate('/invoiceDetails', { state: { id: invoice?._id } });
          },
          status: <BakeasyChip label={invoice?.status} />,
          storepo: invoice?.poNumber,
          orderDate: invoice?.orderDate ? formatDateTime(invoice?.orderDate) : '',
          shippedDate: invoice?.shippedDate ? formatDateTime(invoice?.shippedDate) : '',
          // onShippedMemoClick: () => {
          //   navigate('/invoiceDetails', { state: { id: invoice?._id } });
          // },
          invoicedDate: invoice?.invoiceDate ? formatDateTime(invoice?.invoiceDate) : '',
          invoicedValue: parseFloat(invoice?.totalSumInInvoice).toFixed(2),
          totalPrice: parseFloat(invoice?.totalAmount).toFixed(2),
          deliveryDate: invoice?.requestedDeliveryDate,
          isSubOrderPresent: invoice?.isSubOrderPresent
        };
      });

      setInvoiceList(filteredData);
      setCount(value?.totalCount);
    }
  }, [value]);

  const handleHistoryRowIconClick = async (id: any) => {
    try {
      let payload = { ...id };
      await dispatch(fetchInvoicesInnerTable(payload));
    } catch (err) { }
  };

  useEffect(() => {
    if (historyValue) {
      const filteredDataa = historyValue?.data?.map((data: any) => {
        return {
          invoiceId: data?.invoiceId,
          subOrderId: data?.subOrderId,
          onSubOrderIdClick: () => {
            navigate('/invoiceDetails', { state: { id: data?.parentOrderId } });
          },
          status: <BakeasyChip label={data?.status} />,
          orderDate: data?.orderDate ? formatDateTime(data?.orderDate) : '',
          invoicedValue: parseFloat(data?.invoicedValue).toFixed(2),
          invoicedDate: data?.invoicedDate ? formatDateTime(data?.invoicedDate) : '',
          shipDate: data?.shippedDate ? formatDateTime(data?.shippedDate) : ''
        };
      });

      setInvoiceHistoryList(filteredDataa);

      if (filteredDataa && filteredDataa.length > 0 && invoiceList?.length > 0) {
        let p1: any = invoiceList?.map((invoice: any) => invoice?._id === historyValue?._id);
        p1['history'] = filteredDataa;

        let sdf = invoiceList;
        sdf?.forEach((invoice: any) => {
          if (invoice?.id === historyValue?._id) {
            invoice.history = filteredDataa;
          }
          return invoice;
        });
        setInvoiceList(sdf);
      }
    }
  }, [historyValue]);

  function openFilterInvoiceConfirmDialog() {
    myContext.setConfirmDialog({
      type: 'invoice-filter',
      open: true,
      data: filter,
      title: t('INVOICE_FILTER'),
      onYesClick: (event: any, param: string | undefined) => {
        setTFilter(param);
        refreshPageData(0, search, param);
      },
      onNoClick: () => { }
    });
  }

  const handleDelete = async (key: any) => {
    // const newFilters = { ...tFilter };
    // delete newFilters[key];
    let newFilters = await deleteKeyFromObject(key, tFilter);
    setTFilter(newFilters);
    refreshPageData(pageNo - 1, search, newFilters);
  };

  function invoicesUI() {
    return (
      <Grid>
        <BakeasyPaper>
          <BakeasyHeaderLabel message={t('INVOICES')} />

          <Grid
            container
            spacing={2}
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
              <TextField.Search
                value={search}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setSearch(event?.target?.value);
                  if (event?.target?.value?.length === 0 || event?.target?.value?.length > 2) {
                    refreshPageData(
                      pageNo - 1,
                      event?.target?.value?.length === 0 ? undefined : event?.target?.value,
                      filter
                    );
                  }
                }}
                onIconClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                  refreshPageData(pageNo - 1, search, filter);
                }}
                onEnterClick={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  refreshPageData(pageNo - 1, search, filter);
                }}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={6} lg={6} xl={3}>
              <div className="tableOptions" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button.Print
                  onClick={function (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
                    dispatch(downloadInvoices({ ...filter, downloadType: 'pdf' }));
                  }} />
                <Button.Export
                  onClick={function (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
                    dispatch(downloadInvoices({ ...filter, downloadType: 'csv' }));
                  }}
                />
                <Button.Filter
                  onClick={function (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
                    openFilterInvoiceConfirmDialog();
                  }}
                />
              </div>
            </Grid>
          </Grid>

          <BakeasySelectedFilters filter={filter} onDelete={handleDelete} />

          <Grid paddingTop={2}>
            {invoiceList?.length > 0 ? (
              <BakeasyCollapsibleTable
                menu={'invoices'}
                pageNo={pageNo - 1}
                search={search}
                tableHeaders={invoiceTableHeaders}
                reportTableHeaders={reportTableHeaders}
                rowsData={invoiceList}
                count={count}
                refreshPageData={refreshPageData}
                filter={filter}
              />
            ) : (
              <BakeasyEmptyMsg message={t('NO_DATA_FOUND')} />
            )}
          </Grid>
        </BakeasyPaper>
      </Grid>
    );
  }
  return invoicesUI();
};
